// Product analytics consent by region (docs/analytics.md). Nothing is sent to Mixpanel before it is allowed:
//   EU and EEA (with their outermost regions), UK, Switzerland, the Crown dependencies and Gibraltar, Quebec (and a
//   Canadian visit without a province), a few countries with consent first laws, and an unknown location: off until
//   the visitor turns them on (Settings, Privacy, or Analytics choices in the footer). Everywhere else: on from the
//   first page, session replay included, with no banner: the Privacy Policy explains it and people agree to it when
//   they join (MG, 2026-10-07); anyone can turn it off in the same two places. Global Privacy Control and Do Not
//   Track always mean off in this browser, and the visitor is never asked.
// The location comes from <meta name="mg-geo" content="CC-REGION"> that the Cloudflare Pages middleware adds to every
// page (frontend/functions/_middleware.js). The choice lives in this browser (mgs-analytics-consent: state, time,
// version) and, for a signed in person, on the server (consent_records, purpose analytics) so it follows them.

/** The analytics notice version; same value as backend app/legal.py ANALYTICS_CONSENT_VERSION. A new version asks
 *  again where consent comes first (a "no" always stays a no). */
export const ANALYTICS_CONSENT_VERSION = '2026-10-07'
export const ANALYTICS_CONSENT_KEY = 'mgs-analytics-consent'
/** The time of an answer given in this tab while nobody was signed in (sessionStorage, so it ends with the visit): it
 *  becomes the account's answer only for whoever signs in during the same visit, never for the next person who signs
 *  in on this computer later (re-audit minor 14). */
export const VISIT_CHOICE_KEY = 'mgs-analytics-consent-visit'

// EU 27, the rest of the EEA (Iceland, Liechtenstein, Norway), the UK and Switzerland, plus the places Cloudflare
// reports under their own code where the GDPR applies (Aland, French Guiana, Guadeloupe, Martinique, Reunion,
// Mayotte, Saint Martin) or a GDPR equivalent law does (Gibraltar, Jersey, Guernsey, the Isle of Man).
const EUROPE = ['AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE', 'IT', 'LV', 'LT',
  'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE', 'IS', 'LI', 'NO', 'GB', 'CH',
  'AX', 'GF', 'GP', 'MQ', 'RE', 'YT', 'MF', 'GI', 'JE', 'GG', 'IM']
// Consent first for analytics cookies elsewhere: Brazil (LGPD, ANPD guidance), China (PIPL), South Korea (PIPA),
// Thailand (PDPA guidance), Turkey (KVKK cookie guideline), Vietnam (Decree 13). MG can trim this list.
const CONSENT_FIRST = ['BR', 'CN', 'KR', 'TH', 'TR', 'VN']
export const OPT_IN_COUNTRIES: ReadonlySet<string> = new Set([...EUROPE, ...CONSENT_FIRST])
/** Provinces and states that need consent first inside an opt out country: Quebec (Law 25, privacy by default). */
export const OPT_IN_REGIONS: ReadonlySet<string> = new Set(['CA-QC'])

export type Geo = { country: string | null; region: string | null }
export type ConsentSource = 'banner' | 'settings' | 'notice' | 'gpc' | 'dnt'
/**
 * The answer this browser holds.
 * - `adopted`: it came from the signed in person's account (another device), not from this browser.
 * - `owner`: the user id of the signed in person it belongs to (adopted from their account, or given here while signed
 *   in). The browser's own earlier answer comes back when the session ends (consentStore setViewer and signOut), so
 *   the next person on a shared computer never runs on it, and it is never sent to another account.
 * - `pending`: given in this browser and not yet confirmed by the account. `at` is then the browser's clock; once the
 *   account confirmed it, `at` is the account's time for it (the server's clock), and browser and server times are
 *   never compared with each other.
 * - `basis`: the time of the account's answer this browser knew when the person answered here (null: none known), so
 *   an answer made on another device after it is recognised without comparing clocks.
 */
export type StoredChoice = {
  state: 'granted' | 'denied'
  at: string
  version: string
  source: ConsentSource
  adopted?: boolean
  owner?: string
  pending?: boolean
  basis?: string | null
}
export type ServerChoice = { granted: boolean; source: string; version: string; at: string }
export type PrivacySignal = 'gpc' | 'dnt' | null
export type Decision = {
  allowed: boolean
  /** opt-in: consent first (off until the person turns it on); opt-out: on by default */
  mode: 'opt-in' | 'opt-out'
  reason: 'choice' | 'signal' | 'default' | 'pending'
}

/** "CA-QC" (country and region), "DE", or empty: Cloudflare's request.cf country and regionCode. */
export function parseGeo(raw: string | null | undefined): Geo {
  const [country, region] = (raw ?? '').trim().toUpperCase().split('-', 2)
  const cc = /^[A-Z]{2}$/.test(country ?? '') && country !== 'XX' && country !== 'T1' ? country : null
  const rc = cc && region && /^[A-Z0-9]{1,3}$/.test(region) ? region : null
  return { country: cc, region: rc }
}

/** The geo meta of this page, or the e2e test hook (dev server only, no middleware there). */
export function readGeo(doc: Document | undefined = globalThis.document): Geo {
  const hook = import.meta.env.DEV ? testHook()?.geo : undefined
  if (hook !== undefined) return parseGeo(hook)
  return parseGeo(doc?.querySelector('meta[name="mg-geo"]')?.getAttribute('content'))
}

/** Unknown location counts as consent first: we cannot tell the visitor is outside those places. So does a country
 *  with a consent first region when Cloudflare gives no region (a Canadian visit may come from Quebec). */
export function needsOptIn(geo: Geo): boolean {
  if (!geo.country) return true
  if (OPT_IN_COUNTRIES.has(geo.country)) return true
  if (!geo.region) return [...OPT_IN_REGIONS].some((r) => r.startsWith(`${geo.country}-`))
  return OPT_IN_REGIONS.has(`${geo.country}-${geo.region}`)
}

/** navigator.globalPrivacyControl (https://globalprivacycontrol.org) first, then Do Not Track. */
export function privacySignal(nav: Navigator | undefined = globalThis.navigator, win: unknown = globalThis.window): PrivacySignal {
  const n = nav as (Navigator & { globalPrivacyControl?: unknown; msDoNotTrack?: unknown }) | undefined
  if (n?.globalPrivacyControl === true) return 'gpc'
  const dnt = [n?.doNotTrack, n?.msDoNotTrack, (win as { doNotTrack?: unknown } | undefined)?.doNotTrack]
  return dnt.some((v) => v === '1' || v === 'yes' || v === 1) ? 'dnt' : null
}

/** e2e and dev only: window.__MGS_ANALYTICS_TEST__ = { token, geo, replayPercent } set before the app loads. */
export type AnalyticsTestHook = { token?: string; geo?: string; replayPercent?: number }
export function testHook(): AnalyticsTestHook | undefined {
  if (!import.meta.env.DEV) return undefined
  return (globalThis as { __MGS_ANALYTICS_TEST__?: AnalyticsTestHook }).__MGS_ANALYTICS_TEST__
}

export function readChoice(storage: Storage | undefined = safeStorage()): StoredChoice | null {
  try {
    const raw = storage?.getItem(ANALYTICS_CONSENT_KEY)
    return parseChoice(raw ? (JSON.parse(raw) as unknown) : null)
  } catch {
    return null
  }
}

/** A stored answer from JSON, or null when it is not one. */
export function parseChoice(value: unknown): StoredChoice | null {
  const v = value as Partial<StoredChoice> | null
  if (!v || typeof v !== 'object' || (v.state !== 'granted' && v.state !== 'denied') || typeof v.at !== 'string') return null
  return {
    state: v.state, at: v.at, version: String(v.version ?? ''), source: (v.source ?? 'banner') as ConsentSource,
    ...(v.adopted === true ? { adopted: true } : {}),
    ...(typeof v.owner === 'string' && v.owner ? { owner: v.owner } : {}),
    ...(v.pending === true ? { pending: true } : {}),
    ...(typeof v.basis === 'string' || v.basis === null ? { basis: v.basis } : {}),
  }
}

export function writeChoice(choice: StoredChoice | null, storage: Storage | undefined = safeStorage()): void {
  try {
    if (choice) storage?.setItem(ANALYTICS_CONSENT_KEY, JSON.stringify(choice))
    else storage?.removeItem(ANALYTICS_CONSENT_KEY)
  } catch {
    /* private mode or full storage: the banner asks again next time */
  }
}

/** The rules in one place: a privacy signal, then a "no", then a valid "yes", then the region's default. A yes that
 *  only came from the notice (on by default elsewhere) is not consent where consent comes first. */
export function decide(geo: Geo, signal: PrivacySignal, stored: StoredChoice | null): Decision {
  const mode = needsOptIn(geo) ? 'opt-in' : 'opt-out'
  if (signal) return { allowed: false, mode, reason: 'signal' }
  if (stored?.state === 'denied') return { allowed: false, mode, reason: 'choice' }
  const current = stored?.version === ANALYTICS_CONSENT_VERSION
  if (stored?.state === 'granted' && current && !(mode === 'opt-in' && stored.source === 'notice')) {
    return { allowed: true, mode, reason: 'choice' }
  }
  return mode === 'opt-in' ? { allowed: false, mode, reason: 'pending' } : { allowed: true, mode, reason: 'default' }
}

export type ConsentPost = { granted: boolean; source: ConsentSource; version: string }
/** `post`: what to record on the account. `adopt`: the account's answer to take here. `settle`: the account already
 *  holds this browser's answer, which takes the account's time for it (no longer pending). */
export type Reconciled = { post: ConsentPost | null; adopt: StoredChoice | null; settle: string | null }

/**
 * What to do for a signed in person (`userId`), given the account's answer and this browser's. An explicit answer
 * (banner, Settings, a browser signal) outranks the notice. Browser and server clocks are never compared:
 * - an answer given here and not yet on the account (pending) is sent when it is at least as strong as the account's,
 *   and between two explicit answers that differ, when it was given after the account's answer this browser knew
 *   (`basis`); when that order is unknown (another device answered meanwhile, or it was given while signed out) a no
 *   wins over a yes;
 * - an answer the account already confirmed gives way to whatever the account says now (another device changed it);
 * - only this person's answer is ever sent: never one adopted from an account, nor one given by someone else signed in
 *   on this computer, nor one given while nobody was signed in except during this visit (`visit`, the time of the
 *   answer given in this tab while signed out: VISIT_CHOICE_KEY); an older answer of the browser stays the browser's.
 * Where analytics are on by default, a signed in person with no answer anywhere gets the notice row: the Privacy
 * Policy they agreed to when they joined is the notice (MG, 2026-10-07), and the server sends their events from then
 * on. A yes from the notice is not adopted where consent comes first. The version sent is the one the person answered.
 */
export function reconcile(server: ServerChoice | null, local: StoredChoice | null, decision: Decision,
  signal: PrivacySignal, userId?: string | null, visit?: string | null): Reconciled {
  const none: Reconciled = { post: null, adopt: null, settle: null }
  if (signal) {
    // the browser's signal decides here; it becomes the account's answer when there is none yet, and replaces a yes
    // that only came from the notice (an explicit yes made in the banner or Settings stays the account's answer)
    const noticeYes = !!server && server.granted && server.source === 'notice'
    if (server && !noticeYes) return none
    return { ...none, post: { granted: false, source: signal, version: ANALYTICS_CONSENT_VERSION } }
  }
  const counts = (c: StoredChoice) => (c.state === 'denied' || c.version === ANALYTICS_CONSENT_VERSION)
    // a yes from the notice is no answer where consent comes first
    && !(decision.mode === 'opt-in' && c.source === 'notice' && c.state === 'granted')
  const theirs = (c: StoredChoice) => (c.owner ? c.owner === userId : !!c.pending && !!visit && c.at === visit)
  const own = local && !local.adopted && theirs(local) && counts(local) ? local : null
  const post = (c: StoredChoice): Reconciled => ({ ...none, post: { granted: c.state === 'granted', source: c.source, version: c.version } })
  const adopt = (s: ServerChoice): Reconciled => {
    if (s.granted && s.source === 'notice' && decision.mode === 'opt-in') return none
    return { ...none, adopt: { state: s.granted ? 'granted' : 'denied', at: s.at, version: s.version, source: s.source as ConsentSource } }
  }
  const rank = (source: string) => (source === 'notice' ? 0 : 1)
  if (!server && !local && decision.mode === 'opt-out' && decision.reason === 'default') {
    return { ...none, post: { granted: true, source: 'notice', version: ANALYTICS_CONSENT_VERSION } }
  }
  if (own?.pending) {
    if (!server) return post(own)
    const stronger = rank(own.source) - rank(server.source)
    const same = server.granted === (own.state === 'granted')
    if (same && stronger <= 0) return { ...none, settle: server.at }
    if (stronger > 0) return post(own)
    if (stronger < 0) return adopt(server)
    // two answers of the same kind that differ: the newer one, when this browser can tell
    if (own.source === 'notice') return post(own) // OK here: the notice of the region this browser is in
    if (own.owner && own.owner === userId && (own.basis ?? null) === server.at) return post(own)
    return own.state === 'denied' ? post(own) : adopt(server)
  }
  if (own) {
    if (!server) return post(own)
    return server.at === own.at ? none : adopt(server)
  }
  return server ? adopt(server) : none
}

/** Remembers (or forgets, null) the answer given in this tab while nobody was signed in. */
export function markVisitChoice(at: string | null, storage: Storage | undefined = safeStorage('sessionStorage')): void {
  try {
    if (at) storage?.setItem(VISIT_CHOICE_KEY, at)
    else storage?.removeItem(VISIT_CHOICE_KEY)
  } catch {
    /* storage blocked: that answer stays this browser's only */
  }
}

export function readVisitChoice(storage: Storage | undefined = safeStorage('sessionStorage')): string | null {
  try {
    return storage?.getItem(VISIT_CHOICE_KEY) ?? null
  } catch {
    return null
  }
}

function safeStorage(name: 'localStorage' | 'sessionStorage' = 'localStorage'): Storage | undefined {
  try {
    return globalThis[name]
  } catch {
    return undefined
  }
}
